import { useCallback, useRef, useState } from 'react';
import moment from 'moment';
import { errorMessages } from '../../../utils/methods';
import { withOverlapConfirm } from '../../../utils/overlapConfirm';
import { clearScheduleClipboard, copyToScheduleClipboard, useScheduleClipboard } from './scheduleClipboardStore';
import {
  COPYABLE_KINDS,
  PAST_PASTE_MESSAGE,
  PRIVATE_KINDS,
  isPastPasteTarget,
  buildClipboardEntry,
  checkTrainerAssignment,
  loadGroupPasteMembers,
  pasteSchedule,
  previewGroupPaste,
} from './pasteSchedule';
import { getTicketsByClassTime } from '../../../api/ticket';
import { isOutOfTicketPeriod } from '../privateLesson/ScheduleMoveConfirm';

const ACTIVE_RESERVATION_STATUSES = ['RESERVED', 'ATTENDED', 'ABSENT'];

const KIND_LABEL = {
  PRIVATE_LESSON: '개인레슨',
  DUET_LESSON: '듀엣레슨',
  OT: 'OT',
  GROUP_LESSON: '그룹수업',
  CONSULTATION: '상담',
  ETC: '기타 일정',
};

const OTHER_BRANCH_PASTE_MESSAGE = '다른 지점에서 복사한 일정은 붙여넣을 수 없습니다.';

const NO_PERMISSION_MESSAGE = '일정을 복사·붙여넣을 권한이 없습니다.';

const targetLabel = target => {
  const date = moment(target.date, 'YYYY-MM-DD').format('M월 D일(ddd)');
  // 지점 스케줄 30분 단위 칸의 빈칸 붙여넣기는 startHour 와 함께 분 단위 startTime 을 준다
  if (target.startHour != null && target.startTime) return `${date} ${target.startTime}`;
  return target.startHour == null ? date : `${date} ${String(target.startHour).padStart(2, '0')}시`;
};

/**
 * 스케줄 복사·붙여넣기(#143) 오케스트레이션. 지점 스케줄과 그룹수업 화면이 같이 쓴다.
 *
 * @param canPaste 생성·수정 권한. 없으면 복사 메뉴·붙여넣기 메뉴·드래그 복사를 모두 막는다.
 * @param allowedPasteKinds 이 화면에 붙여넣을 수 있는 유형. 그룹수업 화면은 그룹수업만 보이므로 제한한다.
 * @param onPasted 성공 후 목록 갱신 콜백
 * @param onConflict 시간 충돌로 거부됐을 때 부딪힌 일정 정보({type, id, date, startTime, endTime})로 불린다
 * @param formatConflictError (message, conflict) => 토스트 내용. 충돌 에러 토스트에 화면별 안내를 붙일 때(지점 스케줄의 숨긴 유형 안내)
 * @param canDelete 카드 우클릭 메뉴에 '일정 삭제'를 보일지 — boolean 또는 event => boolean(유형별 수정 권한). 복사 권한이 없어도 메뉴는 열린다
 */
export const useSchedulePaste = ({
  token,
  branchId,
  toast,
  onPasted,
  onConflict,
  formatConflictError,
  canPaste = true,
  canDelete = false,
  allowedPasteKinds = COPYABLE_KINDS,
}) => {
  const clipboard = useScheduleClipboard();
  const [menu, setMenu] = useState(null);
  const [groupAsk, setGroupAsk] = useState(null);
  // 복사할 날짜가 원본 예약 수강권 이용기간 밖일 때 띄우는 팝업 — 이동과 같은 규칙(사용자 결정 2026-10-07)
  const [periodAsk, setPeriodAsk] = useState(null); // { info, targetDate }
  const [groupPreview, setGroupPreview] = useState(null);
  const [trainerAsk, setTrainerAsk] = useState(null);
  const [resultMembers, setResultMembers] = useState(null);
  const [busy, setBusyState] = useState(false);
  // 중복 요청 방지 — state 는 다음 렌더에야 반영되므로 연타를 즉시 막으려고 ref 를 함께 쓴다
  const busyRef = useRef(false);
  const setBusy = useCallback(value => {
    busyRef.current = value;
    setBusyState(value);
  }, []);

  const closeMenu = useCallback(() => setMenu(null), []);

  /** 카드 우클릭 — 복사·삭제 대상. 둘 다 못 하는 카드·유형이면 기본 브라우저 메뉴를 그대로 둔다. */
  const openCardMenu = useCallback(
    (e, event) => {
      if (!event?.courseClassType || !COPYABLE_KINDS.includes(event.courseClassType)) return;
      const deletable = typeof canDelete === 'function' ? !!canDelete(event) : !!canDelete;
      if (!canPaste && !deletable) return;
      e.preventDefault();
      e.stopPropagation();
      setMenu({
        position: { left: e.clientX, top: e.clientY },
        mode: 'card',
        event,
        canCopy: canPaste,
        canDelete: deletable,
      });
    },
    [canPaste, canDelete],
  );

  /** 빈칸 우클릭 — 붙여넣기 대상. target = { date, startHour, trainerId, trainerName } */
  const openEmptyMenu = useCallback(
    (e, target) => {
      if (!canPaste) return;
      e.preventDefault();
      e.stopPropagation();
      setMenu({ position: { left: e.clientX, top: e.clientY }, mode: 'empty', target });
    },
    [canPaste],
  );

  const copy = useCallback(() => {
    if (!menu?.event) return;
    // 복사한 지점을 함께 싣는다 — 다른 지점에서는 붙여넣지 못하게 한다
    const entry = { ...buildClipboardEntry(menu.event), branchId };
    copyToScheduleClipboard(entry);
    toast.success(`${KIND_LABEL[entry.kind] || '일정'}을 복사했습니다. 빈칸을 우클릭해 붙여넣으세요.`);
  }, [branchId, menu, toast]);

  const execute = useCallback(
    async (entry, target, options, successMessage) => {
      if (busyRef.current) return;
      setBusy(true);
      try {
        // 미리보기에서 겹침을 이미 확인했으면(options.force) 바로 force 로 보낸다
        const result = await withOverlapConfirm(force =>
          pasteSchedule({ entry, target, token, branchId, options: { ...options, force: options.force || force } }),
        );

        if (result.kind === 'GROUP_LESSON' && result.requestedMemberCount > 0) {
          // 응답 members 는 성공 회원까지 포함한다 — 모달에는 빠진 회원만 보여준다.
          const excluded = (result.memberResults || []).filter(member => member.failures?.length > 0);
          const reserved = result.requestedMemberCount - excluded.length;
          toast.success(`붙여넣었습니다. 회원 ${reserved}명 예약${excluded.length > 0
            ? `, ${excluded.length}명 제외` : ''}`);
          // 미리보기에서 이미 보여준 제외 명단과 같으면 다시 띄우지 않는다 — 그 사이 바뀐 경우(동시 처리)만 알린다.
          const expected = options.expectedExcludedIds;
          const changed = !expected
            || expected.length !== excluded.length
            || excluded.some(member => !expected.includes(member.memberId));
          if (excluded.length > 0 && changed) {
            setResultMembers(excluded);
          }
        } else {
          toast.success(successMessage || '붙여넣었습니다.');
        }

        setGroupAsk(null);
        setGroupPreview(null);
        setTrainerAsk(null);
        await onPasted?.();
      } catch (error) {
        // 겹침 확인창에서 취소했으면 안내 없이 끝낸다(묻던 모달은 그대로 둔다)
        if (error?.silent) return;
        const conflict = error?.response?.data?.conflict;
        // 안내를 붙여도 토스트는 하나 — 두 개를 띄우면 같은 자리에 겹쳐 에러 문구가 가려진다
        toast.error(
          conflict && formatConflictError ? formatConflictError(errorMessages(error), conflict) : errorMessages(error),
        );
        if (conflict) {
          setGroupAsk(null);
          setGroupPreview(null);
          setTrainerAsk(null);
          onConflict?.(conflict);
        }
      } finally {
        setBusy(false);
      }
    },
    [branchId, formatConflictError, onConflict, onPasted, setBusy, toast, token],
  );

  /** 회원까지 붙여넣기 — 바로 만들지 않고 새 회차 기준 판정(예약 가능·제외 사유)을 먼저 보여준다. */
  const openGroupPreview = useCallback(async () => {
    if (!groupAsk || busyRef.current) return;
    setBusy(true);
    try {
      let forced = false;
      const preview = await withOverlapConfirm(force => {
        forced = force;
        return previewGroupPaste({
          entry: groupAsk.entry,
          target: groupAsk.target,
          token,
          branchId,
          members: groupAsk.members,
          force,
        });
      });
      setGroupPreview({
        entry: groupAsk.entry,
        target: groupAsk.target,
        sourceMembers: groupAsk.members,
        results: preview.members,
        // 겹침을 이미 확인했으면 실제 붙여넣기에서 다시 묻지 않는다
        force: forced,
      });
      setGroupAsk(null);
    } catch (error) {
      if (error?.silent) return;
      const conflict = error?.response?.data?.conflict;
      toast.error(
        conflict && formatConflictError ? formatConflictError(errorMessages(error), conflict) : errorMessages(error),
      );
      if (conflict) {
        setGroupAsk(null);
        onConflict?.(conflict);
      }
    } finally {
      setBusy(false);
    }
  }, [branchId, formatConflictError, groupAsk, onConflict, setBusy, toast, token]);

  /** 항목 하나를 target 에 만든다 — 유형에 따라 먼저 물어볼 것이 있으면 모달을 띄운다. 붙여넣기·드래그 복사가 같이 쓴다. */
  const runPaste = useCallback(
    async (entry, target, successMessage) => {
      if (busyRef.current) return;
      if (!canPaste) {
        toast.error(NO_PERMISSION_MESSAGE);
        return;
      }
      // 다른 지점에서 복사한 일정은 이 지점의 강의실·강사·상품과 맞지 않는다 — 막고 클립보드를 비운다
      if (entry.branchId != null && entry.branchId !== branchId) {
        toast.error(OTHER_BRANCH_PASTE_MESSAGE);
        clearScheduleClipboard();
        return;
      }
      // 지난 시간으로는 복사·붙여넣기하지 않는다 — 회원 질문 모달을 띄우기 전에 막는다
      let isPast = false;
      try {
        isPast = isPastPasteTarget(entry, target);
      } catch (error) {
        toast.error(errorMessages(error));
        return;
      }
      if (isPast) {
        toast.error(PAST_PASTE_MESSAGE);
        return;
      }
      if (entry.kind === 'GROUP_LESSON') {
        setBusy(true);
        try {
          const members = await loadGroupPasteMembers({ entry, token });
          setGroupAsk({ entry, target, members });
        } catch (error) {
          toast.error(errorMessages(error));
        } finally {
          setBusy(false);
        }
        return;
      }

      if (PRIVATE_KINDS.includes(entry.kind) || entry.kind === 'OT') {
        setBusy(true);
        try {
          // 원본 회원의 예약 수강권 이용기간 밖 날짜로는 복사하지 않는다(다른 유효 수강권이 있어도 이동과 같이 막는다)
          if (entry.courseClassId) {
            const rows = (await getTicketsByClassTime(entry.courseClassId, token)) || [];
            const out = rows.find(
              row =>
                ACTIVE_RESERVATION_STATUSES.includes(row.status) &&
                isOutOfTicketPeriod(row.ticketStartDate, row.ticketExpireDate, target.date),
            );
            if (out) {
              setPeriodAsk({
                info: {
                  memberId: out.memberId,
                  memberName: out.memberName,
                  ticketName: out.courseTicketName,
                  startDate: out.ticketStartDate,
                  expireDate: out.ticketExpireDate,
                  memberCourseTicketId: out.memberCourseTicketId,
                },
                targetDate: target.date,
              });
              return;
            }
          }
          const check = await checkTrainerAssignment({ entry, target, token, branchId });
          if (check?.needsAssign) {
            setTrainerAsk({
              entry,
              successMessage,
              target,
              targets: check.targets || [],
              trainerName: target.trainerId ? target.trainerName : entry.trainerName,
            });
            return;
          }
        } catch (error) {
          toast.error(errorMessages(error));
          return;
        } finally {
          setBusy(false);
        }
      }

      await execute(entry, target, {}, successMessage);
    },
    [branchId, canPaste, execute, setBusy, toast, token],
  );

  /** 붙여넣기 */
  const paste = useCallback(async () => {
    const target = menu?.target;
    if (!clipboard || !target) return;

    if (!allowedPasteKinds.includes(clipboard.kind)) {
      toast.error(`이 화면에는 ${allowedPasteKinds.map(kind => KIND_LABEL[kind]).join('·')}만 붙여넣을 수 있습니다.`);
      return;
    }
    await runPaste(clipboard, target);
  }, [allowedPasteKinds, clipboard, menu, runPaste, toast]);

  /** 드래그 복사 — 카드(event)를 클립보드를 거치지 않고 바로 target 에 복사한다. target.startTime 은 분 단위 시작 시각. */
  const copyEventTo = useCallback(
    (event, target, successMessage) =>
      runPaste({ ...buildClipboardEntry(event), branchId }, target, successMessage),
    [branchId, runPaste],
  );

  return {
    clipboard,
    busy,
    menu,
    openCardMenu,
    openEmptyMenu,
    closeMenu,
    copy,
    paste,
    copyEventTo,
    periodAsk,
    closePeriodAsk: () => setPeriodAsk(null),
    groupAsk,
    trainerAsk,
    resultMembers,
    groupAskLabel: groupAsk ? targetLabel(groupAsk.target) : '',
    groupAskMemberCount: groupAsk?.members?.length ?? 0,
    groupAskCapacity: groupAsk?.entry?.capacity ?? null,
    onGroupSelect: includeMembers =>
      includeMembers ? openGroupPreview() : execute(groupAsk.entry, groupAsk.target, { members: [] }),
    groupPreview,
    groupPreviewLabel: groupPreview ? targetLabel(groupPreview.target) : '',
    onGroupPreviewConfirm: () =>
      execute(groupPreview.entry, groupPreview.target, {
        members: groupPreview.sourceMembers,
        force: groupPreview.force,
        expectedExcludedIds: groupPreview.results
          .filter(member => member.failures?.length > 0)
          .map(member => member.memberId),
      }),
    closeGroupPreview: () => setGroupPreview(null),
    onTrainerSelect: assignTrainer =>
      execute(trainerAsk.entry, trainerAsk.target, { assignTrainer }, trainerAsk.successMessage),
    closeGroupAsk: () => setGroupAsk(null),
    closeTrainerAsk: () => setTrainerAsk(null),
    closeResult: () => setResultMembers(null),
  };
};
